"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { TrendingUp, ArrowRight, Target, ChartBar as BarChart3, Layers, UserCheck, Sparkles, ChevronRight, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const caseStudies = [{
  id: "case-1",
  title: "National B2B SaaS Growth & Lead Engine",
  clientType: "Enterprise B2B Software",
  timeline: "8 Months",
  category: "National SEO & Technical",
  challenge: "Stagnant organic leads, heavy reliance on paid search campaigns, and severe technical indexation issues across 1,200+ landing pages.",
  strategy: "Comprehensive technical overhaul, algorithmic keyword intent mapping, and B2B high-conversion content architecture.",
  implementation: "Resolved Core Web Vitals bottlenecks, re-architected internal linking pathways, and built 24 targeted comparison hubs.",
  resultsMetric: "+284%",
  resultsLabel: "Organic Lead Increase",
  secondaryMetric: "#1 Rank",
  secondaryLabel: "Target B2B Keywords (UK)",
  businessImpact: "Reduced paid advertising spend by £14,000 monthly while increasing qualified demo inquiries by 3.2x.",
  quote: "Yassine's direct technical expertise delivered more qualified sales pipeline in 6 months than our previous agency achieved in two years.",
  author: "Marketing Lead",
  company: "B2B Tech Solutions"
}, {
  id: "case-2",
  title: "E-commerce Revenue Expansion & Recovery",
  clientType: "UK Luxury Retailer",
  timeline: "12 Months",
  category: "Ecommerce SEO",
  challenge: "Recent search update penalty causing a 45% traffic decline on high-margin product category pages.",
  strategy: "Deep algorithmic audit, EEAT signal enhancement, structured product taxonomy restructuring, and high-tier editorial link building.",
  implementation: "Cleaned canonical conflicts, implemented detailed Schema markup across 3,500+ SKUs, and earned top-tier press mentions.",
  resultsMetric: "+310%",
  resultsLabel: "Organic Revenue Growth",
  secondaryMetric: "450k+",
  secondaryLabel: "Monthly Organic Clicks",
  businessImpact: "Fully recovered search visibility and achieved record-breaking Q4 organic sales exceeding projections by 62%.",
  quote: "Working directly with Yassine meant clear answers, zero fluff, and immediate action. The revenue results speak for themselves.",
  author: "Head of Digital",
  company: "Luxury Retail Brand"
}, {
  id: "case-3",
  title: "Multi-Location Regional Market Domination",
  clientType: "Healthcare Services Provider",
  timeline: "6 Months",
  category: "Local & Regional SEO",
  challenge: "Inconsistent local listings, weak Google Map Pack visibility, and local competitors dominating key regional hubs.",
  strategy: "Hyper-local landing page architecture, Geo-targeted entity optimization, and streamlined citation ecosystems.",
  implementation: "Optimized 14 local branch pages, implemented automated local review workflows, and built regional service hubs.",
  resultsMetric: "+195%",
  resultsLabel: "Map Pack Inquiries",
  secondaryMetric: "#1-3",
  secondaryLabel: "Local Search Rankings",
  businessImpact: "Generated over 1,200 direct patient appointment calls across 14 locations without paid local ads.",
  quote: "Yassine transformed our regional search presence. Having a dedicated specialist who understands local search dynamics made all the difference.",
  author: "Operations Lead",
  company: "Regional Health Group"
}];
const trustSignals = [{
  icon: UserCheck,
  title: "Direct Consultant Access",
  desc: "You work directly with me — Yassine Essaadi. No account managers or junior pass-offs."
}, {
  icon: Target,
  title: "Customized Strategy",
  desc: "Tailored SEO roadmap tailored specifically to your revenue drivers and industry competition."
}, {
  icon: ShieldCheck,
  title: "Transparent Execution",
  desc: "Clear deliverables, measurable search milestones, and honest monthly performance tracking."
}];
export default function CaseStudyShowcase() {
  const [activeTab, setActiveTab] = useState(caseStudies[0].id);
  const shouldReduceMotion = useReducedMotion();
  const selectedStudy = caseStudies.find(study => study.id === activeTab) || caseStudies[0];
  return <section id="casestudyshowcase" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            <Sparkles className="size-3.5 text-primary" />
            <span>Verified SEO Client Results</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Real Search Growth, Executed Directly By Me
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Every case study represents genuine search performance achieved for my clients.
            No templated reports, no automated guesswork — just custom SEO strategies structured for high business impact.
          </p>
        </div>

        {/* Case Study Tab Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {caseStudies.map((study, index) => {
          const isActive = study.id === activeTab;
          return <div key={study.id} data-index={index}>
                <Button variant="ghost" type="button" onClick={() => setActiveTab(study.id)} className={`w-full h-auto text-left justify-start p-5 rounded-xl border transition-all duration-200 cursor-pointer flex-col items-start whitespace-normal gap-0 ${isActive ? "bg-card border-primary ring-2 ring-primary/20 shadow-md hover:bg-card" : "bg-background border-border hover:bg-muted/50 hover:border-border/80"}`}>
                  <div className="flex items-center justify-between w-full mb-2">
                    <Badge variant={isActive ? "default" : "secondary"}>
                      {study.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-medium">
                      {study.timeline}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground text-base line-clamp-1 mb-1">
                    {study.title}
                  </h3>
                  <div className="flex items-center text-xs text-muted-foreground gap-1 mt-2 font-medium">
                    <span>{study.clientType}</span>
                    <span>•</span>
                    <span className="text-primary font-semibold">
                      {study.resultsMetric} {study.resultsLabel}
                    </span>
                  </div>
                </Button>
              </div>;
        })}
        </div>

        {/* Case Study Detail View */}
        <div className="mb-16">
          <AnimatePresence mode="wait">
            <motion.div key={selectedStudy.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border-border shadow-lg bg-card overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border p-6 sm:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-primary/30 text-primary">
                          {selectedStudy.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-medium">
                          {selectedStudy.clientType}
                        </span>
                      </div>
                      <CardTitle className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                        {selectedStudy.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm mt-1">
                        Strategy and execution led entirely by Yassine Essaadi (
                        {selectedStudy.timeline} engagement)
                      </CardDescription>
                    </div>

                    {/* Quick Metrics Callout */}
                    <div className="flex items-center gap-4 sm:gap-6 bg-background p-4 sm:p-5 rounded-xl border border-border shrink-0">
                      <div className="text-center sm:text-left">
                        <div className="text-2xl sm:text-3xl font-bold text-primary font-mono tracking-tight">
                          {selectedStudy.resultsMetric}
                        </div>
                        <div className="text-xs text-muted-foreground font-medium">
                          {selectedStudy.resultsLabel}
                        </div>
                      </div>
                      <div className="h-10 w-px bg-border" />
                      <div className="text-center sm:text-left">
                        <div className="text-2xl sm:text-3xl font-bold text-foreground font-mono tracking-tight">
                          {selectedStudy.secondaryMetric}
                        </div>
                        <div className="text-xs text-muted-foreground font-medium">
                          {selectedStudy.secondaryLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6 sm:p-8 space-y-8">
                  {/* Strategy Steps Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Challenge */}
                    <div className="bg-background p-5 rounded-xl border border-border flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-foreground font-semibold mb-3">
                          <Target className="size-4 text-primary shrink-0" />
                          <h4 className="text-sm font-serif">
                            1. The Challenge
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {selectedStudy.challenge}
                        </p>
                      </div>
                    </div>

                    {/* Strategy */}
                    <div className="bg-background p-5 rounded-xl border border-border flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-foreground font-semibold mb-3">
                          <BarChart3 className="size-4 text-primary shrink-0" />
                          <h4 className="text-sm font-serif">
                            2. The SEO Strategy
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {selectedStudy.strategy}
                        </p>
                      </div>
                    </div>

                    {/* Implementation */}
                    <div className="bg-background p-5 rounded-xl border border-border flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-foreground font-semibold mb-3">
                          <Layers className="size-4 text-primary shrink-0" />
                          <h4 className="text-sm font-serif">
                            3. Direct Implementation
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {selectedStudy.implementation}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Impact Box */}
                  <div className="bg-secondary/40 border border-secondary p-6 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-primary text-primary-foreground rounded-lg shrink-0">
                        <TrendingUp className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground text-base mb-1 font-serif">
                          Verified Business Impact
                        </h4>
                        <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                          {selectedStudy.businessImpact}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="border-l-4 border-primary pl-4 sm:pl-6 py-2">
                    <p className="text-base sm:text-lg italic text-foreground font-serif">
                      "{selectedStudy.quote}"
                    </p>
                    <div className="mt-2 text-xs sm:text-sm font-semibold text-muted-foreground">
                      — {selectedStudy.author},{" "}
                      <span className="text-foreground">
                        {selectedStudy.company}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Trust Signals Underneath */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {trustSignals.map((signal, i) => {
          const Icon = signal.icon;
          return <div key={signal.title} data-index={i} className="bg-card p-6 rounded-xl border border-border flex items-start gap-4 shadow-xs">
                <div className="p-3 bg-secondary text-primary rounded-xl shrink-0">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground text-base mb-1 font-serif">
                    {signal.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {signal.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Bottom CTA Banner */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="max-w-3xl mx-auto relative z-10 space-y-6">
            <Badge variant="secondary" className="bg-primary-foreground/10 text-primary-foreground border-none px-3 py-1">
              Work Directly With Yassine
            </Badge>
            <h3 className="text-xl sm:text-2xl font-bold font-serif tracking-tight">
              Ready to Achieve Predictable Organic Search Growth?
            </h3>
            <p className="text-primary-foreground/80 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              Book a free consultation directly with me. We will discuss your
              current SEO performance, search bottlenecks, and custom
              opportunities for growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 font-semibold shadow-md cursor-pointer">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold cursor-pointer">
                <Link to="/CaseStudiesPage">
                  <span>Explore All Case Studies</span>
                  <ChevronRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}